import { createElement, type ReactNode } from "react";
import {
  Book,
  BookOpen,
  BotMessageSquare,
  ChartBar,
  CheckSquare,
  Code,
  GitBranch,
  Layers,
  Link,
  Settings,
  Table2,
  UsersRound,
  Variable,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { ISection } from "@/components/Layouts/StandardLayout";
import { useSpotUser } from "@/utils/spotUser";
import { useIsLocalEnv } from "@/utils/isLocalEnv";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/services/FeatureFlags/FeatureFlags";
import { isNoxusInternal } from "@/utils/isNoxusInternal";
import {
  ORG_PERMISSIONS,
  WORKSPACE_PERMISSIONS,
} from "@/constants/permissions";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import type { BetaFeature } from "@/components/Admin/BetaFeatures/betaFeatures";

// Who may see a section or a sidenav entry.
export interface ISectionGate {
  adminOnly?: boolean;
  noxusOnly?: boolean;
  localhostOnly?: boolean;
  featureFlag?: string;
  betaFeature?: BetaFeature;
  requiredPermissions?: string[];
  level?: "tenant" | "group";
  // All of requiredPermissions rather than any one of them.
  requireAll?: boolean;
  // Organization permissions that also open it, whatever the workspace role
  // (the backend's org-admin fallback).
  orTenantPermissions?: string[];
}

export interface IPageSection extends ISectionGate {
  id: string;
  title: string;
  icon?: LucideIcon;
}

interface ISectionedPage {
  // Where the page lives; pages under a dynamic route (an inbox) build it.
  path?: string;
  sections: readonly IPageSection[];
}

// Every page whose sections are listed in a sidenav. The page renders the
// section the URL's `?tab=` names; the sidenav lists them. Both read this.
export const PAGE_SECTIONS = {
  workshop: {
    path: "/",
    sections: [
      { id: "flows", title: "Flows", icon: Workflow },
      { id: "agents", title: "Agents", icon: BotMessageSquare },
      { id: "knowledge-bases", title: "Knowledge Bases", icon: BookOpen },
      { id: "tables", title: "Tables", icon: Table2 },
    ],
  },
  pastUsage: {
    path: "/past-usage",
    sections: [
      { id: "flows", title: "Flows", icon: Workflow },
      { id: "coworkers", title: "Agents", icon: BotMessageSquare },
    ],
  },
  inbox: {
    sections: [
      { id: "inbox", title: "Cases", icon: Layers },
      { id: "tasks", title: "Tasks", icon: CheckSquare },
      {
        id: "automations",
        title: "Automations",
        icon: Workflow,
        requiredPermissions: [WORKSPACE_PERMISSIONS.CASES.MANAGE_INBOXES],
        level: "group",
      },
      {
        id: "settings",
        title: "Settings",
        icon: Settings,
        requiredPermissions: [WORKSPACE_PERMISSIONS.CASES.MANAGE_INBOXES],
        level: "group",
      },
    ],
  },
  workspace: {
    path: "/manage-workspace",
    sections: [
      {
        id: "about",
        title: "General",
        icon: Book,
        requiredPermissions: [WORKSPACE_PERMISSIONS.ADMIN.WORKSPACE_ADMIN],
        level: "group",
        orTenantPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
      },
      {
        id: "users",
        title: "Members",
        icon: UsersRound,
        requiredPermissions: [
          WORKSPACE_PERMISSIONS.ADMIN.USERS_EDIT,
          WORKSPACE_PERMISSIONS.ADMIN.USERS_DELETE,
          WORKSPACE_PERMISSIONS.ADMIN.WORKSPACE_ADMIN,
        ],
        level: "group",
        orTenantPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
      },
      {
        id: "linked-accounts",
        title: "Connections",
        icon: Link,
        requiredPermissions: [WORKSPACE_PERMISSIONS.ADMIN.INTEGRATIONS_EDIT],
        level: "group",
      },
      {
        id: "variables",
        title: "Variables",
        icon: Variable,
        requiredPermissions: [WORKSPACE_PERMISSIONS.VARIABLES_EDIT],
        level: "group",
      },
      {
        id: "api-keys",
        title: "API keys",
        icon: Code,
        requiredPermissions: [WORKSPACE_PERMISSIONS.ADVANCED],
        level: "group",
      },
      {
        id: "analytics",
        title: "Analytics",
        icon: ChartBar,
        requiredPermissions: [WORKSPACE_PERMISSIONS.ANALYTICS],
        level: "group",
      },
      {
        id: "gitops",
        title: "GitOps",
        icon: GitBranch,
        featureFlag: "GITOPS_ENABLED",
        requiredPermissions: [WORKSPACE_PERMISSIONS.ADMIN.WORKSPACE_ADMIN],
        level: "group",
      },
    ],
  },
  profile: {
    path: "/settings/profile",
    sections: [
      { id: "profile", title: "Profile" },
      { id: "advanced", title: "Advanced" },
    ],
  },
  roles: {
    path: "/settings/administration/roles",
    sections: [
      { id: "organization", title: "Organization" },
      { id: "workspace", title: "Workspace" },
    ],
  },
  orgAnalytics: {
    path: "/settings/observability/analytics",
    sections: [
      { id: "flows", title: "Flows" },
      { id: "agents", title: "Agents" },
      { id: "knowledge-bases", title: "Knowledge Bases" },
      { id: "api", title: "API" },
      { id: "infrastructure", title: "Infrastructure", adminOnly: true },
      { id: "genie", title: "Genie", betaFeature: "genie" },
    ],
  },
  billing: {
    path: "/settings/administration/billing",
    sections: [
      { id: "overview", title: "Overview" },
      { id: "limits", title: "Usage controls" },
      { id: "analytics", title: "Analytics" },
    ],
  },
  models: {
    path: "/settings/platform/models",
    sections: [
      { id: "providers", title: "Providers" },
      { id: "llms", title: "LLMs" },
      { id: "embeddings", title: "Embeddings" },
      { id: "rerankers", title: "Rerankers" },
      { id: "decisions", title: "Decision models" },
      { id: "presets", title: "Presets" },
    ],
  },
  authentication: {
    path: "/settings/platform/authentication",
    sections: [
      { id: "auth", title: "Authentication" },
      { id: "security", title: "Access & security" },
      { id: "saml", title: "Advanced" },
      {
        id: "migration",
        title: "Auth Migration",
        featureFlag: "BETTER_AUTH_MIGRATION_ENABLED",
      },
    ],
  },
  platformIntegrations: {
    path: "/settings/platform/integrations",
    sections: [
      { id: "providers", title: "Providers" },
      { id: "integrations", title: "Integrations" },
    ],
  },
  system: {
    path: "/settings/platform/system",
    sections: [
      { id: "info", title: "Info" },
      { id: "support", title: "Support" },
      { id: "settings", title: "Configurations" },
      { id: "data-retention", title: "Audit logs" },
      { id: "metrics", title: "Metrics" },
      { id: "security", title: "Security" },
      { id: "sandbox", title: "Sandbox" },
    ],
  },
  adminTools: {
    path: "/settings/internal/admin-actions",
    sections: [
      { id: "recovery", title: "Run Recovery" },
      { id: "cache", title: "Cache" },
      { id: "migrations", title: "Migrations" },
      { id: "lifecycle", title: "Model Lifecycle" },
      { id: "db-locks", title: "DB locks" },
      { id: "billing-rectification", title: "Billing rectification" },
      { id: "users", title: "Users" },
      { id: "noxus-provider", title: "Noxus Provider" },
      { id: "local", title: "Local", localhostOnly: true },
    ],
  },
} as const satisfies Record<string, ISectionedPage>;

export type SectionedPage = keyof typeof PAGE_SECTIONS;
export type SectionId<P extends SectionedPage> =
  (typeof PAGE_SECTIONS)[P]["sections"][number]["id"];

export const sectionHref = (path: string, id: string) => `${path}?tab=${id}`;

// The section a page shows when its URL names none.
export const defaultSectionFor = (path: string): string | undefined =>
  Object.values(PAGE_SECTIONS).find(
    (page: ISectionedPage) => page.path === path
  )?.sections[0]?.id;

// Whether a link to a page's section is what is on screen. A page with no
// `?tab=` shows its first section.
export const isSectionHrefActive = (
  href: string,
  pathname: string | null,
  tab: string | null | undefined
) => {
  const [base, query] = href.split("?");
  const wanted = new URLSearchParams(query).get("tab");
  return (
    wanted !== null &&
    pathname === base &&
    (tab ?? defaultSectionFor(base)) === wanted
  );
};

// Whether the user may see what a gate guards. Every sidenav and page section
// decides through this one predicate.
export const useIsGateAllowed = (groupId?: string) => {
  const { spotUser } = useSpotUser();
  const isLocal = useIsLocalEnv();
  const flags = useFeatureFlags();
  const { data: betaFeatures } = useRemoteData(
    DATA_KEYS.BETA_FEATURES,
    {},
    { staleTime: 60_000 }
  );
  const { hasAnyPermission, hasAllPermissions } = usePermissions(groupId);
  return (gate: ISectionGate): boolean => {
    if (gate.adminOnly && !spotUser.is_admin) return false;
    if (gate.noxusOnly && !isNoxusInternal(spotUser.email)) return false;
    if (gate.localhostOnly && !isLocal) return false;
    if (gate.featureFlag && !flags[gate.featureFlag]) return false;
    if (gate.betaFeature && !betaFeatures?.includes(gate.betaFeature))
      return false;
    const perms = gate.requiredPermissions ?? [];
    if (perms.length === 0) return true;
    const level = gate.level ?? "group";
    const granted = gate.requireAll
      ? hasAllPermissions(perms, level)
      : hasAnyPermission(perms, level);
    return (
      granted ||
      (!!gate.orTenantPermissions &&
        hasAnyPermission(gate.orTenantPermissions, "tenant"))
    );
  };
};

// The sections of a page the user may open, in order.
export const useAllowedSections = <P extends SectionedPage>(
  page: P,
  groupId?: string
): readonly IPageSection[] => {
  const isAllowed = useIsGateAllowed(groupId);
  return PAGE_SECTIONS[page].sections.filter((section: IPageSection) =>
    isAllowed(section)
  );
};

// Builds a page's sections: the ones the user may open, in the registry's
// order and with its titles. A section given no content (the page does not
// offer it here) is left out. Call it at the top of the component and build
// the sections where the content is ready, after any early return.
export const useSections = <P extends SectionedPage>(
  page: P,
  groupId?: string
) => {
  const sections = useAllowedSections(page, groupId);
  return (content: Partial<Record<SectionId<P>, ReactNode>>): ISection[] =>
    sections.flatMap((section) => {
      const body = content[section.id as SectionId<P>];
      return body === undefined || body === null || body === false
        ? []
        : [
            {
              id: section.id,
              title: section.title,
              icon:
                section.icon &&
                createElement(section.icon, { size: 16, strokeWidth: 1.25 }),
              content: body,
            },
          ];
    });
};

// Manage workspace sections opened from the main sidenav, which keep it
// instead of switching to Manage workspace's own.
export const MAIN_SIDENAV_WORKSPACE_SECTIONS: readonly SectionId<"workspace">[] =
  ["linked-accounts", "analytics"];
